import { imageDataToimage } from "image-utils";

export const createCanvas = function (width = 210, height = 297) {
  var canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

export const imageToCanvas = function (
  image,
  rotation = 0,
  background = "white",
) {
  var width = image.naturalWidth;
  var height = image.naturalHeight;

  let translateX = 0;
  let translateY = 0;

  if (rotation === 90) {
    [width, height] = [height, width]; //swap
    translateX = width;
  } else if (rotation === 180) {
    translateX = width;
    translateY = height;
  } else if (rotation === 270) {
    [width, height] = [height, width]; //swap
    translateY = height;
  } else if (rotation !== 0) {
    throw new Error("rotation only accepts 90 / 180 / 270");
  }

  let canvas = createCanvas(width, height);
  let context = canvas.getContext("2d");
  if (background) {
    context.fillStyle = background; // white background
    context.fillRect(0, 0, width, height);
  }
  if (rotation !== 0) {
    context.translate(translateX, translateY);
    context.rotate((rotation * Math.PI) / 180);
  }

  context.drawImage(image, 0, 0);

  return canvas;
};

export const rotateCanvas = function (canvas, rotation = 90) {
  var width = canvas.width;
  var height = canvas.height;

  let translateX = 0;
  let translateY = 0;

  if (rotation === 90) {
    [width, height] = [height, width]; //swap
    translateX = width;
  } else if (rotation === 180) {
    translateX = width;
    translateY = height;
  } else if (rotation === 270) {
    [width, height] = [height, width]; //swap
    translateY = height;
  } else if (rotation !== 0) {
    throw new Error("rotation only accepts 90 / 180 / 270");
  }

  let newCanvas = createCanvas(width, height);
  let context = newCanvas.getContext("2d");

  if (rotation !== 0) {
    context.translate(translateX, translateY);
    context.rotate((rotation * Math.PI) / 180);
  }

  context.drawImage(canvas, 0, 0);

  return newCanvas;
};

export const imageDataToCanvas = function (
  imageData,
  rotation = 0,
  background = "white",
) {
  return imageDataToimage(imageData).then(function (image) {
    return imageToCanvas(image, rotation, background);
  });
};

export const resizeCanvas = function (
  canvas,
  maxWidth,
  maxHeight,
  maintainRatio = true,
) {
  var width, height;
  if (maintainRatio) {
    var scale; // scale to fit a max dimensions (fill width or height)
    if (canvas.width / canvas.height < maxWidth / maxHeight) {
      scale = maxHeight / canvas.height;
    } else {
      scale = maxWidth / canvas.width;
    }
    width = canvas.width * scale;
    height = canvas.height * scale;
  } else {
    width = maxWidth;
    height = maxHeight;
  }
  //img can be canvas or image
  var resize = function (img, width, height) {
    var _canvas, ctx;
    _canvas = document.createElement("canvas");
    ctx = _canvas.getContext("2d");
    _canvas.width = width;
    _canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    return _canvas;
  };
  //keep downsizing by half where possible for better quality
  while (canvas.width / 2 > width) {
    canvas = resize(canvas, canvas.width / 2, canvas.height / 2);
  }
  //keep upsizing by 2 where possible for better quality
  while (canvas.width * 2 < width) {
    canvas = resize(canvas, canvas.width * 2, canvas.height * 2);
  }
  //resize to exact dims
  canvas = resize(canvas, width, height);
  return canvas;
};

export const writeText = function (
  ctx,
  text,
  x,
  y,
  font = "10px sans-serif",
  color = "black",
  align = "start",
  baseline = "alphabetic",
) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.font = font;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  ctx.fillText(text, x, y);
  ctx.restore();
};

export const drawRectangle = function (
  ctx,
  x,
  y,
  width,
  height,
  fillStyle = "blue",
  border = {},
  shadow = {},
) {
  //(off by default)
  let borderDefaults = {
    width: 0, //lineWidth
    style: "black", //strokeStyle
    corners: "round", //lineJoin
  };

  border = Object.assign({}, borderDefaults, border);

  //(off by default)
  let shadowDefaults = {
    color: "transparent", //shadowColor
    blur: 10, //shadowBlur
    offsetX: 5, //shadowOffsetX
    offsetY: 5, //shadowOffsetY
  };

  shadow = Object.assign({}, shadowDefaults, shadow);

  ctx.save();
  ctx.shadowColor = shadow.color;
  ctx.shadowBlur = shadow.blur;
  ctx.shadowOffsetX = shadow.offsetX;
  ctx.shadowOffsetY = shadow.offsetY;
  ctx.lineWidth = border.width;
  ctx.strokeStyle = border.style;
  ctx.lineJoin = border.corners;
  ctx.fillStyle = fillStyle;
  ctx.fillRect(x, y, width, height);
  ctx.shadowColor = "transparent"; //no shadow for border
  ctx.strokeRect(x, y, width, height);
  ctx.restore();
};

export const drawCircle = function (
  ctx,
  x,
  y,
  radius,
  fillStyle = "blue",
  border = {},
  shadow = {},
) {
  //(off by default)
  let borderDefaults = {
    width: 0, //lineWidth
    style: "black", //strokeStyle
    corners: "round", //lineJoin
  };

  border = Object.assign({}, borderDefaults, border);

  //(off by default)
  let shadowDefaults = {
    color: "transparent", //shadowColor
    blur: 10, //shadowBlur
    offsetX: 5, //shadowOffsetX
    offsetY: 5, //shadowOffsetY
  };

  shadow = Object.assign({}, shadowDefaults, shadow);

  ctx.save();
  ctx.shadowColor = shadow.color;
  ctx.shadowBlur = shadow.blur;
  ctx.shadowOffsetX = shadow.offsetX;
  ctx.shadowOffsetY = shadow.offsetY;
  ctx.lineWidth = border.width;
  ctx.strokeStyle = border.style;
  ctx.lineJoin = border.corners;
  ctx.fillStyle = fillStyle;

  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.fill();

  ctx.shadowColor = "transparent"; //no shadow for border
  ctx.stroke();

  ctx.restore();
};

export const drawImageAsCircle = function (
  ctx,
  x,
  y,
  radius,
  image,
  imageOptions = {},
  border,
  shadow,
) {
  let defaultImageOptions = {
    // coords to start drawing from source image (asif square)
    x: 0,
    y: 0,
    // how much of the source image to be used
    width: radius * 2,
    height: radius * 2,
  };

  imageOptions = Object.assign({}, defaultImageOptions, imageOptions);

  // draw shadow
  drawCircle(ctx, x, y, radius, "white", undefined, shadow);

  ctx.save();

  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.clip();

  //draw image
  ctx.drawImage(
    image,
    imageOptions.x,
    imageOptions.y,
    imageOptions.width,
    imageOptions.height,
    x - radius,
    y - radius,
    radius * 2,
    radius * 2,
  );

  ctx.beginPath();
  ctx.arc(x - radius, y - radius, radius, 0, Math.PI * 2, true);
  ctx.clip();
  ctx.closePath();

  ctx.restore();

  // draw border (hides rough edges)
  drawCircle(ctx, x, y, radius, "transparent", border);
};
